import { FormEvent, useState } from 'react';
import { KeyRound, Loader2 } from 'lucide-react';

import { Modal } from '@/components/modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PersonalConnection } from '@/services/personal-connections/types';

/**
 * Write-only API key entry. The key lives only in this input until submit,
 * is cleared on submit / cancel, and is never rendered back: the server
 * keeps it sealed and reports only "Personal API key".
 */
export const ApiKeyDialog = ({
  connection,
  open,
  onOpenChange,
  onSubmit,
  saving,
}: {
  connection: PersonalConnection;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (apiKey: string) => Promise<boolean>;
  saving: boolean;
}) => {
  const [value, setValue] = useState('');
  const spec = connection.api_key;
  if (!spec) return null;
  const inputId = `${connection.provider}-api-key`;

  const close = (next: boolean) => {
    if (!next) setValue('');
    onOpenChange(next);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const key = value;
    setValue('');
    if (await onSubmit(key)) onOpenChange(false);
  };

  return (
    <Modal
      isDialogOpen={open}
      setIsDialogOpen={close}
      title={`Use a ${connection.name} API key`}
      description={`${spec.where_to_create} It starts with ${spec.prefix}. The platform checks it with one read-only call, keeps it encrypted for you alone, and never shows it again.`}
    >
      <form className='flex flex-col gap-3' onSubmit={submit}>
        <Label htmlFor={inputId}>API key</Label>
        <Input
          id={inputId}
          data-testid={`api-key-input-${connection.provider}`}
          type='password'
          autoComplete='off'
          spellCheck={false}
          placeholder={spec.placeholder}
          value={value}
          onChange={(event) => setValue(event.target.value)}
        />
        <div className='flex justify-end gap-2'>
          <Button type='button' variant='outline' onClick={() => close(false)}>
            Cancel
          </Button>
          <Button type='submit' disabled={saving || !value.trim()}>
            {saving ? (
              <Loader2 className='size-4 animate-spin' />
            ) : (
              <KeyRound className='size-4' />
            )}
            Save key
          </Button>
        </div>
      </form>
    </Modal>
  );
};
